import { Stack } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { t } from 'i18next';
import { get } from 'lodash';
import { CSSProperties, forwardRef, ReactNode, SyntheticEvent, useMemo, useState } from 'react';

import { UpsertLocation } from 'contacts/components';
import { LISTBOX_STYLE } from 'contacts/constants';
import { ContactGroupType } from 'contacts/enums';
import { contactGroupService } from 'contacts/services';
import {
  IContactGroup,
  IContactGroupAddress,
  IContactGroupAddressListRequestPayload,
  ILocationForm,
} from 'contacts/types';
import { FormField } from 'core/components';
import { defaultRenderNewOption } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { LabeledValue, NeutralChip, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { getAddressToDisplay } from 'core/utils';
import { Direction, Level } from 'theme/enums';

import { useRenderGroup } from './useRenderGroup';

export interface ILocationFormFieldProps {
  name: string;
  label?: string;
  extraLabel?: JSX.Element;
  placeholder?: string;
  contactGroup?: Partial<IContactGroup>;
  groupOptionsByContactGroup?: boolean;
  requestPayload?: IContactGroupAddressListRequestPayload;
  hidden?: boolean;
  disabled?: boolean;
  required?: boolean;
  disableClearable?: boolean;
  hasCreateOption?: boolean;
  onChange?: (event: SyntheticEvent, option: IContactGroupAddress | IContactGroupAddress[]) => void;
  getLabel?: (option: IContactGroupAddress) => string;
  onAddNewOption?: (option: IAutocompleteOption) => void;
  onCreateNewOption?: (location: IAutocompleteOption) => void;
  level?: number;
  multiple?: boolean;
  wrapperStyle?: CSSProperties;
  getQueryFromValue?: (value: string) => string;
  listboxStyle?: SxProps;
  hideCustomerNumber?: boolean;
  getOptions?: (requestPayload: object, signal?: AbortSignal) => unknown;
  clearInputOnSelect?: boolean;
  endDecorator?: ReactNode;
}

const getDefaultLocationLabel = (option: IContactGroupAddress) => {
  if (!option?.id) {
    return '';
  }

  const label = [option.name];

  if (option?.accountsReceivableId) {
    label.push(`[${option.accountsReceivableId}]`);
  }

  return label.join(' ');
};

const getDefaultQueryFromValue = (label: string) => {
  if (!label) {
    return '';
  }

  const bracketIndex = label.indexOf('[');

  return bracketIndex !== -1 ? label.substring(0, bracketIndex).trim() : label.trim();
};

export const LocationFormField = forwardRef(function LocationFormField(
  {
    name = 'location',
    label = t('labelLocation'),
    extraLabel = null,
    placeholder,
    contactGroup,
    groupOptionsByContactGroup = false,
    requestPayload = {},
    hidden = false,
    disabled = false,
    required = true,
    disableClearable = true,
    hasCreateOption = true,
    multiple = false,
    onChange,
    onAddNewOption,
    onCreateNewOption,
    getLabel = getDefaultLocationLabel,
    getQueryFromValue = getDefaultQueryFromValue,
    level = 0,
    wrapperStyle,
    listboxStyle: customListboxStyle,
    hideCustomerNumber = false,
    getOptions = contactGroupService.getContactGroupAddressesAutocomplete,
    clearInputOnSelect = false,
    endDecorator,
  }: ILocationFormFieldProps,
  ref
) {
  const [isCreateLocationOpen, setIsCreateLocationOpen] = useState(false);
  const [newLocation, setNewLocation] = useState<ILocationForm | null>(null);

  const { control, errors, setValue, getValues } = useFormContext();

  const onAddNewLocation = (option: IAutocompleteOption) => {
    if (!hasCreateOption) {
      return;
    }

    if (onAddNewOption) {
      return onAddNewOption(option);
    }

    setIsCreateLocationOpen(true);
    setNewLocation({ name: option.title });

    setTimeout(() => {
      setValue(name, null);
    }, 1000);
  };

  const handleOnSubmitSuccessAddNewLocation = (contactGroupAddress?: IContactGroupAddress) => {
    if (!contactGroupAddress?.id) {
      setValue(name, multiple ? [] : null);

      return;
    }

    if (multiple) {
      const currentValue = getValues(name) || [];
      setValue(name, [...currentValue, contactGroupAddress]);
    } else {
      setValue(name, contactGroupAddress);
    }
    onCreateNewOption?.(contactGroupAddress);
  };

  const handleOnCloseAddNewLocation = () => {
    setIsCreateLocationOpen(false);
    setNewLocation(null);
  };

  const renderOptions = (
    option: IContactGroupAddress,
    _: (option: IContactGroupAddress) => string,
    inputValue?: string
  ) => {
    // Sentinel options (e.g. "All Customers") use a negative id so they never collide
    // with a real contact group address. They have no AR number / address, so render
    // only the name and skip the labeled-value rows below.
    if (typeof option.id === 'number' && option.id < 0) {
      return (
        <Stack sx={{ width: '100%' }}>
          <TextHighlighter
            level={Level.TITLE_MD}
            text={option.name || DEFAULT_PLACEHOLDER}
            highlight={inputValue}
          />
        </Stack>
      );
    }

    return (
      <Stack sx={{ width: '100%' }}>
        {option.id && (
          <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <TextHighlighter
              level={Level.TITLE_MD}
              text={option.name || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />

            {contactGroup?.id !== option?.contactGroup?.id && option?.contactGroup?.name && (
              <NeutralChip>
                <TextHighlighter
                  level={Level.TITLE_SM}
                  text={option.contactGroup?.name}
                  highlight={inputValue}
                />
              </NeutralChip>
            )}
          </Stack>
        )}

        <LabeledValue
          label={t('labelARNumber')}
          value={
            <TextHighlighter
              level={Level.BODY_SM}
              text={option.accountsReceivableId || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />
          }
          options={{ labelLevel: Level.BODY_SM, labelSx: { fontWeight: 400 }, inline: true }}
        />

        {!hideCustomerNumber && (
          <LabeledValue
            label={t('labelCustomerNumber')}
            value={
              <TextHighlighter
                level={Level.BODY_SM}
                text={option.externalCustomerNumber || DEFAULT_PLACEHOLDER}
                highlight={inputValue}
              />
            }
            options={{ labelLevel: Level.BODY_SM, labelSx: { fontWeight: 400 }, inline: true }}
          />
        )}

        <TextHighlighter
          level={Level.BODY_SM}
          text={getAddressToDisplay(option, {
            displayStateName: true,
            displayCountryName: true,
          }).join(', ')}
          highlight={inputValue}
          sx={{ mt: 0.5 }}
        />
      </Stack>
    );
  };

  const groupBy = (option: IContactGroupAddress & IAutocompleteOption) => {
    if (option.isNew) {
      return 'new';
    }

    return option.mainContactGroup ? 'main' : 'other';
  };

  const renderGroup = useRenderGroup({
    contactGroupName: contactGroup?.name,
    mainGroupI18nKey: 'messageLocationsAssociatedWithContactGroup',
    otherGroupI18nKey: 'messageLocationsAssociatedWithOtherContactGroups',
  });

  const groupByParams = useMemo(() => {
    if (!groupOptionsByContactGroup || !contactGroup?.id) {
      return null;
    }

    return { groupBy, renderGroup };
  }, [groupOptionsByContactGroup, contactGroup?.id, renderGroup]);

  return (
    <>
      <FormField
        ref={ref}
        name={name}
        type={InputType.AUTOCOMPLETE}
        hidden={hidden}
        wrapperStyle={wrapperStyle}
        inputProps={{
          control,
          error: get(errors, name),
          label,
          extraLabel,
          placeholder,
          renderOptions,
          renderNewOption: (option: IAutocompleteOption) =>
            defaultRenderNewOption(option, t('messageAddNewLocation')),
          getOptions,
          onChange,
          getLabel,
          onNewOption: onAddNewLocation,
          requestPayload,
          required,
          disabled,
          hasCreateOption,
          multiple,
          disableClearable,
          includeFilterQuery: true,
          listboxStyle: { ...LISTBOX_STYLE, ...customListboxStyle },
          getQueryFromValue,
          clearInputOnSelect,
          endDecorator,
          ...groupByParams,
        }}
      />

      {hasCreateOption && (
        <UpsertLocation
          isOpen={isCreateLocationOpen}
          onSubmitSuccess={handleOnSubmitSuccessAddNewLocation}
          onClose={handleOnCloseAddNewLocation}
          initialValues={newLocation}
          contactGroup={contactGroup}
          contactGroupType={
            contactGroup
              ? contactGroup.isCustomer
                ? ContactGroupType.CUSTOMER
                : ContactGroupType.VENDOR
              : null
          }
          level={level + 1}
        />
      )}
    </>
  );
});
